import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiPenTool, FiMonitor, FiPrinter, FiTrendingUp } = FiIcons;

const Services = () => {
  const services = [
    {
      title: 'Branding & Identity',
      description: 'Create memorable brand identities that resonate with your target audience and stand out in the competitive South African market.',
      features: [
        'Logo Design & Brand Identity',
        'Brand Strategy & Positioning',
        'Brand Guidelines Development',
        'Rebranding & Brand Refresh'
      ],
      startingPrice: 'From R15,000',
      icon: FiPenTool,
      link: '/services/branding',
      color: 'from-blue-500 to-purple-600'
    },
    {
      title: 'Web Design & Development',
      description: 'Build responsive, user-friendly websites that convert visitors into customers and drive business growth.',
      features: [
        'Custom Website Design',
        'E-commerce Solutions',
        'Mobile-Responsive Development',
        'CMS Integration',
        'SEO Optimization'
      ],
      startingPrice: 'From R25,000',
      icon: FiMonitor,
      link: '/services/web-design',
      color: 'from-green-500 to-teal-600'
    },
    {
      title: 'Print Design',
      description: 'Professional print materials that make a lasting impression and effectively communicate your brand message.',
      features: [
        'Business Stationery',
        'Brochures & Catalogues',
        'Packaging Design',
        'Exhibition Materials',
        'Annual Reports'
      ],
      startingPrice: 'From R5,000',
      icon: FiPrinter,
      link: '/services/print-design',
      color: 'from-orange-500 to-red-600'
    },
    {
      title: 'Digital Marketing',
      description: 'Strategic digital campaigns that drive engagement, increase brand awareness, and grow your business online.',
      features: [
        'Social Media Strategy',
        'Content Creation',
        'Google Ads Management',
        'Email Marketing',
        'Analytics & Reporting'
      ],
      startingPrice: 'From R8,000/month',
      icon: FiTrendingUp,
      link: '/services/digital-marketing',
      color: 'from-pink-500 to-rose-600'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-6">
              Our <span className="text-purple-600">Services</span>
            </h1>
            <p className="font-inter text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              From brand identity to digital marketing, we offer comprehensive creative solutions to elevate your business and help you achieve your goals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden card-hover border border-gray-100"
              >
                <div className={`h-2 bg-gradient-to-r ${service.color}`}></div>
                <div className="p-8">
                  <div className="flex items-center mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center mr-4`}>
                      <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="font-poppins font-bold text-2xl text-gray-900">
                        {service.title}
                      </h2>
                      <p className="font-inter text-purple-600 font-semibold">
                        {service.startingPrice}
                      </p>
                    </div>
                  </div>
                  <p className="font-inter text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="mb-8">
                    <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-4">
                      What's Included:
                    </h3>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-gray-600">
                          <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
                          <span className="font-inter">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link
                    to={service.link}
                    className="inline-flex items-center justify-center w-full px-6 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105"
                  >
                    Learn More <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Our Process
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              We follow a proven 5-step process to ensure every project delivers exceptional results that exceed expectations.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {[
              { step: '01', title: 'Discover', description: 'Client consultation and market research' },
              { step: '02', title: 'Strategy', description: 'Creative strategy and concept development' },
              { step: '03', title: 'Create', description: 'Design concepts and client presentation' },
              { step: '04', title: 'Refine', description: 'Feedback integration and optimization' },
              { step: '05', title: 'Deliver', description: 'Final delivery and implementation support' }
            ].map((process, index) => (
              <motion.div
                key={process.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-poppins font-bold text-white">{process.step}</span>
                </div>
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2">
                  {process.title}
                </h3>
                <p className="font-inter text-gray-600 text-sm">
                  {process.description}
                </p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/process"
              className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105"
            >
              Learn About Our Process <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="font-inter text-lg text-gray-300 max-w-2xl mx-auto mb-8">
              Let's discuss your project and create a custom solution that perfectly fits your needs and budget.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105"
            >
              Get a Free Quote <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;